"use client";

import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTireReading } from "@/app/context/TireReadingContext";
import { Minus, Plus } from "lucide-react";

interface TireAdjustmentModalProps {
  onClose: () => void;
  onConfirm: (
    reason: string,
    newDepth: number,
    newWidth?: number,
    newHeight?: number,
    newRim?: number
  ) => void;
  initialDepth: number;
}

const reasons = [
  "Verkeerde lezing",
  "Andere waarde",
  "Spijker",
  "Scheur",
  "Droogte",
];

// Generate options for select fields
const widthOptions = Array.from({ length: 20 }, (_, i) => 115 + i * 10);
const heightOptions = Array.from({ length: 11 }, (_, i) => 30 + i * 5);
const rimOptions = Array.from({ length: 13 }, (_, i) => 12 + i);

const TireAdjustmentModal: React.FC<TireAdjustmentModalProps> = ({
  onClose,
  onConfirm,
  initialDepth,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const { currentReading } = useTireReading();

  const [depth, setDepth] = useState<number>(
    currentReading?.minProfileDepth || initialDepth
  );
  const [width, setWidth] = useState<number>(currentReading?.tire_width || 205);
  const [height, setHeight] = useState<number>(
    currentReading?.tire_height || 45
  );
  const [rim, setRim] = useState<number>(currentReading?.rim_diameter || 17);

  // Auto-confirm for reasons other than "Verkeerde lezing"
  const handleReasonSelect = (reason: string) => {
    setSelectedReason(reason);
    
    if (reason !== "Verkeerde lezing") {
      // Auto-confirm for simple reasons
      onConfirm(reason, NaN);
      onClose();
    }
  };

  const handleConfirm = () => {
    if (!selectedReason || !currentReading) return;

    const changes: Parameters<typeof onConfirm> = [selectedReason, NaN];

    const depthChanged = depth !== currentReading.minProfileDepth;
    const widthChanged = width !== currentReading.tire_width;
    const heightChanged = height !== currentReading.tire_height;
    const rimChanged = rim !== currentReading.rim_diameter;

    const includeSizes = selectedReason === "Verkeerde lezing";

    changes[1] =
      selectedReason === "Verkeerde lezing" && depthChanged ? depth : NaN;
    if (includeSizes && widthChanged) changes[2] = width;
    if (includeSizes && heightChanged) changes[3] = height;
    if (includeSizes && rimChanged) changes[4] = rim;

    onConfirm(...changes);
    onClose();
  };
  // until 10
  const quickSet = [
    1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10,
  ];

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className={`bg-white p-6 rounded-xl w-full max-w-[90%] relative ${
          selectedReason === "Verkeerde lezing" ? "grid grid-cols-2 gap-6" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 cursor-pointer transition-all duration-200 ease-in-out w-12 h-12 text-5xl flex items-center justify-center"
        >
          X
        </button>

        {/* Left column - Reasons */}
        <div
          className={`flex flex-col gap-8 ${
            selectedReason !== "Verkeerde lezing" ? "w-full" : ""
          }`}
        >
          <h2 className="text-2xl font-bold mb-2">Reden voor wijziging</h2>
          {reasons.map((reason) => (
            <button
              key={reason}
              onClick={() => handleReasonSelect(reason)}
              className={`px-12 py-12 rounded-xl text-left text-4xl font-medium transition-colors cursor-pointer
                ${
                  selectedReason === reason
                    ? "bg-[var(--granu-green)] text-white"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
            >
              {reason}
            </button>
          ))}
        </div>

        {/* Right column - Conditional Inputs */}
        {selectedReason === "Verkeerde lezing" && (
          <div className="flex flex-col items-center justify-between h-full">
            {/* Profile depth */}
            <div className="flex flex-col mb-4 justify-center items-center gap-4">
              <label className="block font-medium mb-4 text-3xl">
                Profiel diepte
              </label>
              <div className="flex gap-4 mb-2 items-center justify-between w-full">
                <button
                  className="w-16 h-16 rounded-xl bg-gray-300 font-bold flex items-center justify-center"
                  onClick={() =>
                    setDepth((prev) =>
                      Math.max(0, parseFloat((prev - 0.1).toFixed(1)))
                    )
                  }
                >
                  <Minus size={32} />
                </button>
                <div className="px-6 py-3 rounded-xl bg-[var(--granu-green)] text-white font-semibold text-2xl h-16 flex items-center justify-center">
                  {depth.toFixed(1)} MM
                </div>
                <button
                  className="w-16 h-16 rounded-xl bg-gray-300 font-bold flex items-center justify-center"
                  onClick={() =>
                    setDepth((prev) => parseFloat((prev + 0.1).toFixed(1)))
                  }
                >
                  <Plus size={32} />
                </button>
              </div>
              <div className="grid grid-cols-4 gap-4">
                {quickSet.map((val) => (
                  <button
                    key={val}
                    onClick={() => setDepth(val)}
                    className={`px-2 py-2 rounded-lg text-2xl font-semibold cursor-pointer transition-colors
                      ${
                        depth === val
                          ? "bg-gray-400 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                  >
                    {val} MM
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col mb-4 justify-center items-center w-full">
              <label className="block font-medium text-3xl">
                Aanpassing maat
              </label>
              {/* Tire size adjustments */}
              <div className="grid grid-cols-3 gap-4 mt-6 w-full">
                <div>
                  <label className="block text-2xl font-medium mb-1">
                    Breedte
                  </label>
                  <select
                    value={width}
                    onChange={(e) => setWidth(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-6 py-4 text-3xl h-20"
                  >
                    {widthOptions.map((w) => (
                      <option
                        key={w}
                        value={w}
                        style={{ fontSize: "2rem", padding: "1rem" }}
                      >
                        {w}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-2xl font-medium mb-1">
                    Hoogte
                  </label>
                  <select
                    value={height}
                    onChange={(e) => setHeight(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-6 py-4 text-3xl h-20"
                  >
                    {heightOptions.map((h) => (
                      <option
                        key={h}
                        value={h}
                        style={{ fontSize: "2rem", padding: "1rem" }}
                      >
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-2xl font-medium mb-1">
                    Velgmaat
                  </label>
                  <select
                    value={rim}
                    onChange={(e) => setRim(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-6 py-4 text-3xl h-20"
                  >
                    {rimOptions.map((r) => (
                      <option
                        key={r}
                        value={r}
                        style={{ fontSize: "2rem", padding: "1rem" }}
                      >
                        R{r}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirm button - only show for "Verkeerde lezing" */}
        {selectedReason === "Verkeerde lezing" && (
          <div className="mt-6 col-span-2 flex justify-center w-full">
            <button
              onClick={handleConfirm}
              disabled={!selectedReason}
              className="bg-[var(--granu-green)] text-white px-12 py-12 rounded-2xl text-4xl font-bold disabled:opacity-50 min-w-[200px] min-h-[64px] w-full"
            >
              OK
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default TireAdjustmentModal;
